/* © ИП Коробкин И.Н., KIN DIGITAL CRM+ */
import { useMutation } from '@tanstack/react-query';
import { FileText, NotebookPen, Send } from 'lucide-react';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Markdown } from '@/help/Markdown';
import { useAiStatus } from './AiChat';
import { Badge, Button, ErrorText, Textarea } from './ui';
import { api } from '@/lib/api';

interface Msg { role: 'user' | 'assistant'; content: string; sources?: any[]; question?: string }

const EXAMPLES = ['Когда ближайшее заседание и что к нему готово?', 'Какие требования кредиторов ещё не рассмотрены?', 'Какие сроки истекают в ближайшие две недели?', 'Что суд написал о задатке?'];

/** M31 «Чат с делом»: вопросы на естественном языке по карточке, документам и расшифровкам, ответ со ссылками. */
export function CaseChat({ matterId, partyId }: { matterId?: string; partyId?: string }) {
  const { data: st } = useAiStatus();
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [text, setText] = useState('');
  const end = useRef<HTMLDivElement>(null);
  const ask = useMutation({
    mutationFn: (q: string) => api.post('/search/ask', { question: q, matterId, partyId, history: msgs.slice(-6).map(({ role, content }) => ({ role, content })) }),
    onSuccess: (r: any, q) => setMsgs((m) => [...m, { role: 'assistant', content: r.answer, sources: r.sources, question: q }]),
  });
  const note = useMutation({ mutationFn: (m: Msg) => api.post('/search/note', { matterId, partyId, question: m.question ?? '', answer: m.content }) });
  useEffect(() => { end.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs, ask.isPending]);
  const send = (q: string) => { if (!q.trim()) return; setMsgs((m) => [...m, { role: 'user', content: q.trim() }]); setText(''); ask.mutate(q.trim()); };
  const submit = (e: FormEvent) => { e.preventDefault(); send(text); };
  if (st && !st.enabled) return <div className="card p-5 text-sm text-slate-600">ИИ не подключён. Администратору: укажите модель в data\settings.env.</div>;
  return (
    <div className="card flex h-[65vh] flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 px-4 py-2 text-xs text-slate-500">
        Ответы строятся по карточке, документам и расшифровкам {matterId ? 'дела' : 'лица'}, со ссылками на источники.
        {st && (st.local ? <Badge tone="green">локальная модель</Badge> : <Badge tone="amber">внешняя модель</Badge>)}
      </div>
      <div className="flex-1 space-y-3 overflow-y-auto p-4">
        {!msgs.length && <div className="flex flex-wrap gap-2">{EXAMPLES.map((e) => <button key={e} onClick={() => send(e)} className="rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600 hover:bg-slate-50">{e}</button>)}</div>}
        {msgs.map((m, i) => (
          <div key={i} className={m.role === 'user' ? 'ml-auto max-w-[80%] rounded-lg bg-brand-50 px-3 py-2 text-sm' : 'max-w-[90%] text-sm'}>
            {m.role === 'user' ? m.content : <>
              <div className="prose-sm"><Markdown text={m.content} /></div>
              {!!m.sources?.length && <div className="mt-2 space-y-1 border-t border-slate-100 pt-2 text-xs text-slate-500">
                {m.sources.map((s: any) => <div key={s.ref} className="flex gap-1"><b>[{s.ref}]</b>
                  {s.documentId ? <Link to={`/documents?open=${s.documentId}`} className="flex items-center gap-1 text-brand-700 hover:underline"><FileText size={12} />{s.title}</Link> : s.title}
                  {s.via && <span className="text-slate-400">· найдено по {s.via.join(' и ')}</span>}</div>)}
              </div>}
              <button className="mt-1 flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800" onClick={() => note.mutate(m)}><NotebookPen size={12} /> {note.isSuccess && note.variables === m ? 'Сохранено в ленту' : 'Сохранить в ленту'}</button>
            </>}
          </div>
        ))}
        {ask.isPending && <div className="text-sm text-slate-400">Ищу в материалах...</div>}
        <ErrorText error={ask.error} />
        <div ref={end} />
      </div>
      <form onSubmit={submit} className="flex gap-2 border-t border-slate-200 p-3">
        <Textarea rows={2} className="flex-1" value={text} onChange={(e) => setText(e.target.value)} placeholder="Спросите о деле: когда заседание, какие требования не рассмотрены, что написал суд..."
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(text); } }} />
        <Button type="submit" disabled={ask.isPending || !text.trim()}><Send size={16} /></Button>
      </form>
    </div>
  );
}
